import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TennisCoachAvatar } from "./TennisCoachAvatar";
import { FLOW_START, FLOW_STEPS } from "./flowSteps";
import { MessageCircle, X, RotateCcw, Minus, Send } from "lucide-react";

const LS_KEY = "tabellone_chatbot_state_v1";

// Lightweight keyword-based matcher so the user can also TYPE a question and
// land on the most relevant predefined step.
const KEYWORD_MAP = [
  { keys: ["circolo", "venue", "logo", "loghi", "sponsor", "fitp"], next: "venue_setup" },
  { keys: ["torneo", "tornei", "creare un torneo", "nuovo torneo"], next: "create_tournament" },
  { keys: ["categoria", "categorie"], next: "add_categories" },
  { keys: ["giocator", "import", "excel", "template", "carica"], next: "import_players" },
  { keys: ["ranking", "classifica", "4.nc"], next: "explain_ranking" },
  { keys: ["tabellone", "draw", "bracket", "tds", "teste di serie"], next: "generate_bracket" },
  { keys: ["qualific"], next: "explain_qualifiers" },
  { keys: ["per ranking", "sezioni", "fitp ranking"], next: "explain_per_ranking" },
  { keys: ["pronostico", "favore"], next: "faq_pronostico" },
  { keys: ["aspettito", "aspèttito"], next: "faq_aspettito" },
  { keys: ["bye"], next: "faq_bye" },
  { keys: ["calendario", "programmaz", "match", "incontri", "schedul"], next: "schedule_matches" },
  { keys: ["riposo", "rest", "tempo riposo"], next: "explain_rest" },
  { keys: ["risultato", "punteggio", "vincitore"], next: "record_results" },
  { keys: ["pdf", "stampa", "esport"], next: "export_pdf" },
  { keys: ["primi passi", "iniziare", "come iniziare", "guida"], next: "first_steps" },
  { keys: ["regolament", "fitp"], next: "faq_fitp" },
  { keys: ["contatt", "supporto", "help", "aiuto", "email", "whatsapp", "telefono", "facebook"], next: "contact_us" },
];

function matchUserInput(text) {
  const t = (text || "").toLowerCase().trim();
  if (!t) return null;
  for (const entry of KEYWORD_MAP) {
    if (entry.keys.some((k) => t.includes(k))) return entry.next;
  }
  return null;
}

const OnboardingChatbot = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [history, setHistory] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch { /* ignore */ }
    return [{ role: "bot", stepKey: FLOW_START }];
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist history
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(history)); } catch { /* ignore */ }
  }, [history]);

  // Auto-scroll bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, isOpen]);

  const handleOptionClick = (opt) => {
    setHistory(prev => [...prev, { role: "user", text: opt.label }]);

    if (opt.navigate) {
      const target = opt.navigate;
      const isExternal = /^(https?:|mailto:|tel:|whatsapp:)/i.test(target);
      setTimeout(() => {
        if (isExternal) {
          window.open(target, "_blank", "noopener,noreferrer");
        } else {
          navigate(target);
        }
      }, 250);
    }

    if (opt.next && FLOW_STEPS[opt.next]) {
      setTimeout(() => {
        setHistory(prev => [...prev, { role: "bot", stepKey: opt.next }]);
      }, 350);
    }

    if (opt.end) {
      setTimeout(() => setIsOpen(false), 400);
    }
  };

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text) return;
    setInputValue("");
    setHistory(prev => [...prev, { role: "user", text }]);

    const matchedStep = matchUserInput(text);
    setTimeout(() => {
      if (matchedStep) {
        setHistory(prev => [...prev, { role: "bot", stepKey: matchedStep }]);
      } else {
        // Free-text fallback: insert a synthetic bot message and go back to menu.
        setHistory(prev => [
          ...prev,
          { role: "bot_inline", text: "Non sono sicuro di aver capito. Provo a rimandarti al menu principale: scegli una voce qui sotto oppure scrivi una parola chiave (es. \"torneo\", \"loghi\", \"calendario\", \"pdf\")." },
          { role: "bot", stepKey: FLOW_START },
        ]);
      }
    }, 350);
  };

  const handleReset = () => {
    setHistory([{ role: "bot", stepKey: FLOW_START }]);
    setInputValue("");
  };

  const lastBotStep = (() => {
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].role === "bot") return FLOW_STEPS[history[i].stepKey];
    }
    return FLOW_STEPS[FLOW_START];
  })();

  return (
    <>
      {/* FAB - sempre visibile */}
      {!isOpen && (
        <button
          data-testid="onboarding-chatbot-fab"
          onClick={() => { setIsOpen(true); setIsMinimized(false); }}
          aria-label="Apri assistente"
          className="fixed bottom-5 right-5 z-[60] h-16 w-16 rounded-full bg-white shadow-2xl border-2 border-green-700 hover:scale-105 active:scale-95 transition-transform group"
        >
          <span className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#c8e414] text-slate-900 text-[10px] font-bold flex items-center justify-center shadow">?</span>
          <span className="absolute inset-1 rounded-full overflow-hidden flex items-center justify-center">
            <TennisCoachAvatar size={56} animate />
          </span>
        </button>
      )}

      {/* Dialog */}
      {isOpen && (
        <div
          data-testid="onboarding-chatbot-dialog"
          className={`fixed z-[60] bottom-5 right-5 w-[420px] max-w-[calc(100vw-2.5rem)] bg-white rounded-md shadow-2xl border border-slate-200 flex flex-col overflow-hidden ${isMinimized ? "h-14" : "h-[640px] max-h-[calc(100vh-2.5rem)]"}`}
          style={{ transition: "height 0.2s ease" }}
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-3 py-2 bg-green-700 text-white border-b border-green-800">
            <div className="h-9 w-9 rounded-full bg-white shrink-0 flex items-center justify-center overflow-hidden">
              <TennisCoachAvatar size={36} />
            </div>
            <div className="flex-1 leading-tight">
              <p className="font-bold text-sm">Coach Bruno</p>
              <p className="text-[10px] text-green-100 flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#c8e414] animate-pulse" /> online · assistente
              </p>
            </div>
            <button
              data-testid="onboarding-chatbot-reset"
              onClick={handleReset}
              title="Ricomincia la conversazione"
              className="p-1.5 hover:bg-green-800 rounded-sm transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="onboarding-chatbot-minimize"
              onClick={() => setIsMinimized(m => !m)}
              title={isMinimized ? "Espandi" : "Minimizza"}
              className="p-1.5 hover:bg-green-800 rounded-sm transition-colors"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="onboarding-chatbot-close"
              onClick={() => setIsOpen(false)}
              title="Chiudi"
              className="p-1.5 hover:bg-green-800 rounded-sm transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {!isMinimized && (
            <>
              {/* Conversation */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50"
              >
                {history.map((msg, idx) => {
                  if (msg.role === "user") {
                    return (
                      <div key={idx} className="flex justify-end">
                        <div className="bg-green-100 text-slate-800 rounded-md rounded-tr-none px-3 py-1.5 text-xs max-w-[80%]">
                          {msg.text}
                        </div>
                      </div>
                    );
                  }
                  if (msg.role === "bot_inline") {
                    return (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="h-7 w-7 shrink-0 rounded-full overflow-hidden bg-white border border-slate-200">
                          <TennisCoachAvatar size={28} />
                        </div>
                        <div className="bg-white border border-slate-200 rounded-md rounded-tl-none px-3 py-2 text-xs text-slate-700 max-w-[85%] whitespace-pre-line">
                          {msg.text}
                        </div>
                      </div>
                    );
                  }
                  const step = FLOW_STEPS[msg.stepKey];
                  if (!step) return null;
                  return (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="h-7 w-7 shrink-0 rounded-full overflow-hidden bg-white border border-slate-200">
                        <TennisCoachAvatar size={28} />
                      </div>
                      <div className="bg-white border border-slate-200 rounded-md rounded-tl-none px-3 py-2 text-xs text-slate-700 max-w-[85%] whitespace-pre-line">
                        {step.bot}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Options */}
              {lastBotStep?.options?.length > 0 && (
                <div className="border-t border-slate-200 bg-white p-2 space-y-1.5 max-h-[220px] overflow-y-auto">
                  {lastBotStep.options.map((opt, i) => {
                    const isAction = !!opt.navigate;
                    return (
                      <button
                        key={i}
                        data-testid={`chatbot-option-${i}`}
                        onClick={() => handleOptionClick(opt)}
                        className={`w-full text-left rounded-sm px-3 py-1.5 text-xs font-medium transition-colors border ${
                          isAction
                            ? "bg-green-700 text-white border-green-700 hover:bg-green-800"
                            : "bg-white text-green-800 border-green-700 hover:bg-green-50"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Input field — free-form questions */}
              <div className="border-t border-slate-200 bg-white p-2 flex items-center gap-2">
                <input
                  ref={inputRef}
                  data-testid="chatbot-input"
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Scrivi la tua domanda..."
                  className="flex-1 rounded-sm border border-slate-300 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent"
                />
                <button
                  data-testid="chatbot-send"
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim()}
                  className="h-9 w-9 shrink-0 rounded-sm bg-green-700 hover:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                  title="Invia"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default OnboardingChatbot;
